import { config } from "@/config"
import type { Song, SongsPayload } from "@/types/song"

export type SortMode = "title" | "artist" | "random"

export interface GenreCount {
  name: string
  count: number
}

export interface GenresPayload {
  genres: GenreCount[]
  total: number
}

export interface SongListFilters {
  query: string
  genres: string[]
  sort: SortMode
  seed: number
  ids?: number[]
}

function apiUrl(): URL {
  if (!config.songsApiUrl) {
    throw new Error("VITE_SONGS_API_URL is not configured")
  }
  return new URL(config.songsApiUrl)
}

async function getJson<T>(url: URL): Promise<T> {
  const res = await fetch(url.toString())
  if (!res.ok) {
    throw new Error(`Failed to fetch: ${res.status}`)
  }
  const payload = (await res.json()) as T | { error: string }
  if (payload && typeof payload === "object" && "error" in payload) {
    throw new Error(payload.error)
  }
  return payload as T
}

function applyFilterParams(url: URL, filters: SongListFilters) {
  if (filters.query.trim()) url.searchParams.set("q", filters.query.trim())
  if (filters.genres.length) {
    url.searchParams.set("genre", filters.genres.join(","))
  }
  if (filters.ids?.length) {
    url.searchParams.set("ids", filters.ids.join(","))
  }
  url.searchParams.set("sort", filters.sort)
  if (filters.sort === "random") {
    url.searchParams.set("seed", String(filters.seed))
  }
}

const PAGE_SIZE = 30

/** GAS list API에서 곡 목록을 30개 단위로 페이지네이션해서 가져온다. */
export async function fetchSongsPage(
  filters: SongListFilters,
  page: number
): Promise<SongsPayload> {
  const url = apiUrl()
  url.searchParams.set("action", "list")
  url.searchParams.set("page", String(page))
  url.searchParams.set("limit", String(PAGE_SIZE))
  applyFilterParams(url, filters)
  return getJson<SongsPayload>(url)
}

/** 장르별 집계 + 전체 곡 수를 가져온다 (검색/장르 필터와 무관한 전체 카탈로그 기준). */
export async function fetchGenres(): Promise<GenresPayload> {
  const url = apiUrl()
  url.searchParams.set("action", "genres")
  return getJson<GenresPayload>(url)
}

/** 현재 검색/장르/즐겨찾기 조건에 맞는 곡 중 하나를 서버에서 무작위로 뽑는다. */
export async function fetchRandomSong(
  filters: Omit<SongListFilters, "sort" | "seed">
): Promise<Song | null> {
  const url = apiUrl()
  url.searchParams.set("action", "random")
  applyFilterParams(url, { ...filters, sort: "title", seed: 0 })
  const payload = await getJson<{ song: Song | null }>(url)
  return payload.song
}
